import type { CSSProperties } from 'react';
import { tokens } from '@/design';
import type { SceneSlot } from '@/sdk-stub/gtwin-sdk/types';
import {
  STRUCTURE_SLOTS,
  type StudioSceneItemEntry,
} from './sceneStructure';

export interface StructureSlotTreeProps {
  entries: StudioSceneItemEntry[];
  layerOrder: string[];
  selectedItemId: string | null;
  transferItemIds: ReadonlySet<string>;
  onSelect: (itemId: string) => void;
  onToggleTransfer: (itemId: string, checked: boolean) => void;
  onMove: (itemId: string, direction: 'up' | 'down') => void;
}

const SLOT_LABELS: Record<SceneSlot, string> = {
  basemaps: 'Basemaps',
  terrain: 'Terrain',
  realityData: 'Reality data',
  engineeringModels: 'Engineering models',
  featureLayers: 'Feature layers',
  overlays: 'Overlays',
  environment: 'Environment',
};

export function StructureSlotTree({
  entries,
  layerOrder,
  selectedItemId,
  transferItemIds,
  onSelect,
  onToggleTransfer,
  onMove,
}: StructureSlotTreeProps) {
  const bySlot = new Map<SceneSlot, StudioSceneItemEntry[]>();
  for (const slot of STRUCTURE_SLOTS) bySlot.set(slot, []);
  for (const entry of entries) {
    bySlot.get(entry.slot)?.push(entry);
  }

  return (
    <div data-testid="structure-slot-tree" style={{ overflow: 'auto', flex: 1 }}>
      {STRUCTURE_SLOTS.map((slot) => {
        const slotEntries = bySlot.get(slot) ?? [];
        if (slotEntries.length === 0) return null;
        return (
          <section key={slot} aria-label={SLOT_LABELS[slot]}>
            <div
              style={{
                padding: `${tokens.space[2]}px ${tokens.space[3]}px`,
                fontSize: tokens.font.size.xs,
                fontWeight: tokens.font.weight.semibold,
                color: tokens.color.ink.secondary,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                background: tokens.color.surface[1],
                borderBottom: tokens.border.thin,
              }}
            >
              {SLOT_LABELS[slot]} ({slotEntries.length})
            </div>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {slotEntries.map(({ item }) => {
                const id = String(item.id);
                const title =
                  typeof item.title === 'string' && item.title.trim()
                    ? item.title
                    : id;
                const visible = item.visible !== false;
                const orderIdx = layerOrder.indexOf(id);
                const canUp = orderIdx > 0;
                const canDown = orderIdx >= 0 && orderIdx < layerOrder.length - 1;
                const selected = selectedItemId === id;
                const forTransfer = transferItemIds.has(id);
                return (
                  <li key={id}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: tokens.space[1],
                        borderBottom: tokens.border.thin,
                        background: selected
                          ? tokens.color.surface[2]
                          : tokens.color.surface[0],
                      }}
                    >
                      <input
                        type="checkbox"
                        data-testid={`transfer-check-${id}`}
                        checked={forTransfer}
                        aria-label={`Select ${title} for transfer`}
                        onChange={(e) => onToggleTransfer(id, e.target.checked)}
                        style={{ marginLeft: tokens.space[2] }}
                      />
                      <button
                        type="button"
                        data-testid={`structure-item-${id}`}
                        onClick={() => onSelect(id)}
                        aria-pressed={selected}
                        style={{
                          flex: 1,
                          textAlign: 'left',
                          padding: `${tokens.space[2]}px ${tokens.space[3]}px`,
                          border: 'none',
                          background: 'transparent',
                          cursor: 'pointer',
                          fontFamily: tokens.font.family.ui,
                          fontSize: tokens.font.size.sm,
                          color: tokens.color.ink.primary,
                        }}
                      >
                        <span style={{ opacity: visible ? 1 : 0.5 }}>{title}</span>
                        {!visible && (
                          <span
                            style={{
                              marginLeft: tokens.space[2],
                              fontSize: tokens.font.size.xs,
                              color: tokens.color.ink.muted,
                            }}
                          >
                            hidden
                          </span>
                        )}
                      </button>
                      <button
                        type="button"
                        aria-label={`Move ${title} up`}
                        disabled={!canUp}
                        onClick={() => onMove(id, 'up')}
                        style={iconBtnStyle}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        aria-label={`Move ${title} down`}
                        disabled={!canDown}
                        onClick={() => onMove(id, 'down')}
                        style={iconBtnStyle}
                      >
                        ↓
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

const iconBtnStyle: CSSProperties = {
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  padding: tokens.space[1],
  color: tokens.color.ink.secondary,
  fontSize: tokens.font.size.sm,
};
